import { Plus, Star, Trash2 } from 'lucide-react';
import {
  PERSON_GENDERS,
  PERSON_GENDER_LABELS,
  type GuestMealOption,
  type GuestPartyMemberInput,
} from '@peles/shared';
import { SearchableListSelect } from '../../common/SearchableListSelect';
import { AttendingToggle } from '../AttendingToggle';
import { PhoneInput } from '../../common/PhoneInput';

export interface PartyMembersEditorProps {
  members: GuestPartyMemberInput[];
  onChange: (members: GuestPartyMemberInput[]) => void;
  mealOptions: GuestMealOption[];
  /**
   * Called as a person is marked coming or not coming, beside `onChange`, so
   * the form can move the party's head count with them.
   */
  onAttendingChange?: (member: GuestPartyMemberInput, attending: boolean) => void;
}

const inputClass =
  'w-full px-3 py-2 border border-gray-300 rounded-lg text-base sm:text-sm focus:outline-none focus:ring-2 focus:ring-primary-500';

/**
 * The named people of a party, edited with the guest — while it is being
 * created too, not only once it has been saved. Each has a name, adult or
 * child, a meal from the event's options, whether they are coming (neither
 * pressed is not answered yet), an adult's side of the mechitza (man or
 * woman — pressed again, not said) and, optionally, their own email or phone;
 * one may be starred as the party's main contact.
 */
export function PartyMembersEditor({
  members,
  onChange,
  mealOptions,
  onAttendingChange,
}: PartyMembersEditorProps) {
  const update = (index: number, patch: Partial<GuestPartyMemberInput>) =>
    onChange(members.map((m, i) => (i === index ? { ...m, ...patch } : m)));
  const setAttending = (index: number, attending: boolean) => {
    if (members[index].attending === attending) return;
    onAttendingChange?.(members[index], attending);
    update(index, { attending });
  };
  const setPrimary = (index: number) =>
    onChange(members.map((m, i) => ({ ...m, isPrimary: i === index ? !m.isPrimary : false })));

  return (
    <div className="space-y-3">
      {members.map((member, index) => (
        <div
          key={member.id ?? `new-${index}`}
          className="rounded-xl border border-gray-200 p-3 space-y-2"
          data-testid="party-member"
        >
          <div className="flex items-center gap-2">
            <input
              value={member.name}
              onChange={(e) => update(index, { name: e.target.value })}
              placeholder="Name"
              aria-label={`Party member ${index + 1} name`}
              className={inputClass}
            />
            <button
              type="button"
              onClick={() => setPrimary(index)}
              aria-pressed={!!member.isPrimary}
              aria-label="Main contact"
              title="Main contact"
              className={`p-2 rounded-lg ${member.isPrimary ? 'text-amber-500' : 'text-gray-300 hover:text-gray-500'}`}
            >
              <Star size={18} className={member.isPrimary ? 'fill-amber-400' : ''} aria-hidden />
            </button>
            <button
              type="button"
              onClick={() => onChange(members.filter((_, i) => i !== index))}
              aria-label={`Remove ${member.name || 'party member'}`}
              className="p-2 rounded-lg text-gray-400 hover:text-red-600"
            >
              <Trash2 size={18} aria-hidden />
            </button>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            <div className="inline-flex rounded-lg border border-gray-300 overflow-hidden text-sm">
              {(['ADULT', 'CHILD'] as const).map((type) => (
                <button
                  key={type}
                  type="button"
                  onClick={() => update(index, { memberType: type })}
                  aria-pressed={(member.memberType ?? 'ADULT') === type}
                  className={`flex-1 px-3 py-2 ${
                    (member.memberType ?? 'ADULT') === type
                      ? 'bg-primary-600 text-white'
                      : 'bg-white text-gray-700'
                  }`}
                >
                  {type === 'ADULT' ? 'Adult' : 'Child'}
                </button>
              ))}
            </div>
            {(member.memberType ?? 'ADULT') === 'ADULT' && (
              <div
                role="group"
                aria-label={`${member.name || `Party member ${index + 1}`}: man or woman`}
                className="inline-flex rounded-lg border border-gray-300 overflow-hidden text-sm"
              >
                {PERSON_GENDERS.map((gender) => (
                  <button
                    key={gender}
                    type="button"
                    onClick={() =>
                      update(index, { gender: member.gender === gender ? null : gender })
                    }
                    aria-pressed={member.gender === gender}
                    className={`flex-1 min-h-11 sm:min-h-0 px-3 py-2 ${
                      member.gender === gender
                        ? 'bg-primary-600 text-white'
                        : 'bg-white text-gray-700'
                    }`}
                  >
                    {PERSON_GENDER_LABELS[gender]}
                  </button>
                ))}
              </div>
            )}
            <AttendingToggle
              name={member.name || `Party member ${index + 1}`}
              coming={member.attending ?? null}
              onChange={(coming) => setAttending(index, coming)}
            />
            {mealOptions.length > 0 && (
              <SearchableListSelect
                items={mealOptions}
                value={member.mealOptionId ?? null}
                onChange={(v) => update(index, { mealOptionId: (v as string) || null })}
                getValue={(m) => m.id}
                getLabel={(m) => m.name}
                placeholder="Meal"
                ariaLabel={`Meal for ${member.name || `party member ${index + 1}`}`}
                clearable
              />
            )}
            <input
              type="email"
              value={member.email ?? ''}
              onChange={(e) => update(index, { email: e.target.value || null })}
              placeholder="Email (optional)"
              aria-label={`Party member ${index + 1} email`}
              className={inputClass}
            />
            <PhoneInput
              value={member.phone ?? ''}
              onChange={(phone) => update(index, { phone: phone || null })}
              placeholder="Phone (optional)"
              aria-label={`Party member ${index + 1} phone`}
              className={inputClass}
            />
          </div>
        </div>
      ))}
      <button
        type="button"
        onClick={() => onChange([...members, { name: '', memberType: 'ADULT' }])}
        className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border border-dashed border-gray-300 text-sm text-gray-700 hover:bg-gray-50"
      >
        <Plus size={15} aria-hidden />
        Add a person by name
      </button>
    </div>
  );
}
